import { toCsv } from "../feedback/csv";
import { personNoteId } from "../person-notes/schemas";
import { eventsNewestFirst, type Connection } from "./schemas";

/**
 * The connections list as files the person takes elsewhere (TNG-93): a CSV
 * for a spreadsheet and a vCard 3.0 file for an address book. Built in the
 * browser from what the list already holds; nothing is sent anywhere.
 *
 * Private notes go in only when the person ticks "include my notes". Once a
 * file has left the app it cannot be called back, so the default is the card
 * alone.
 */

export interface ExportOptions {
  /** Notes by person_notes id (`{eventId}__{attendeeId}`). */
  notes?: ReadonlyMap<string, string>;
  includeNotes: boolean;
}

function metAt(connection: Connection): string {
  const titles = eventsNewestFirst(connection)
    .map((tag) => tag.title)
    .filter(Boolean);
  return titles.length > 0 ? `Met at ${titles.join(", ")}` : "";
}

/** The person's notes across the events they met in, newest meeting first. */
function notesFor(connection: Connection, options: ExportOptions): string[] {
  if (!options.includeNotes || !options.notes) return [];
  return eventsNewestFirst(connection)
    .map((tag) => options.notes?.get(personNoteId(tag.eventId, tag.attendeeId)))
    .filter((body): body is string => Boolean(body?.trim()));
}

export function connectionsCsv(
  connections: Connection[],
  options: ExportOptions,
): string {
  const header = [
    "Name",
    "Role",
    "Company",
    "Email",
    "LinkedIn",
    "Met at",
    ...(options.includeNotes ? ["My notes"] : []),
  ];
  const rows = connections.map((connection) => [
    connection.name,
    connection.role ?? "",
    connection.company ?? "",
    connection.email ?? "",
    connection.linkedinUrl ?? "",
    eventsNewestFirst(connection)
      .map((tag) => tag.title)
      .join("; "),
    ...(options.includeNotes
      ? [notesFor(connection, options).join("\n\n")]
      : []),
  ]);
  return toCsv([header, ...rows]);
}

/** RFC 2426 text value: backslash, comma, semicolon and newlines escaped. */
export function escapeVCardText(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/\r\n|\r|\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");
}

/**
 * Folds a content line at 75 octets, as RFC 2425 asks: CRLF and one space
 * before each continuation. Counted in UTF-8 bytes and never inside a
 * character, so an umlaut or an emoji at the boundary survives.
 */
export function foldLine(line: string): string {
  const encoder = new TextEncoder();
  const parts: string[] = [];
  let current = "";
  let bytes = 0;
  // The first line may hold 75 octets; each continuation starts with a space
  // that counts towards its 75.
  let limit = 75;
  for (const character of line) {
    const size = encoder.encode(character).length;
    if (bytes + size > limit) {
      parts.push(current);
      current = character;
      bytes = size;
      limit = 74;
    } else {
      current += character;
      bytes += size;
    }
  }
  parts.push(current);
  return parts.join("\r\n ");
}

/** "Ada Lovelace" -> family "Lovelace", given "Ada". One word is a given name. */
function nameParts(name: string): { family: string; given: string } {
  const words = name.trim().split(/\s+/);
  if (words.length < 2) return { family: "", given: name.trim() };
  return {
    family: words[words.length - 1],
    given: words.slice(0, -1).join(" "),
  };
}

export function connectionVCard(
  connection: Connection,
  options: ExportOptions,
): string {
  const { family, given } = nameParts(connection.name);
  const note = [metAt(connection), ...notesFor(connection, options)]
    .filter(Boolean)
    .join("\n\n");
  const lines = [
    "BEGIN:VCARD",
    "VERSION:3.0",
    `N:${escapeVCardText(family)};${escapeVCardText(given)};;;`,
    `FN:${escapeVCardText(connection.name)}`,
    ...(connection.company
      ? [`ORG:${escapeVCardText(connection.company)}`]
      : []),
    ...(connection.role ? [`TITLE:${escapeVCardText(connection.role)}`] : []),
    ...(connection.email
      ? [`EMAIL;TYPE=INTERNET:${escapeVCardText(connection.email)}`]
      : []),
    // URL is a URI value, not text, so it is not escaped; the backend only
    // stores https LinkedIn links.
    ...(connection.linkedinUrl ? [`URL:${connection.linkedinUrl}`] : []),
    ...(note ? [`NOTE:${escapeVCardText(note)}`] : []),
    "END:VCARD",
  ];
  return lines.map(foldLine).join("\r\n");
}

export function connectionsVCard(
  connections: Connection[],
  options: ExportOptions,
): string {
  return (
    connections.map((c) => connectionVCard(c, options)).join("\r\n") + "\r\n"
  );
}

export function exportFileName(extension: "csv" | "vcf", now: Date): string {
  const date = now.toISOString().slice(0, 10);
  return `connections-${date}.${extension}`;
}

/**
 * Hands the file over: the share sheet where the browser can share files (a
 * phone, so it can go straight into Contacts or a mail), a download
 * otherwise. Returns how it went; a share the person cancelled is not an
 * error.
 */
export async function shareOrDownload(
  file: File,
): Promise<"shared" | "downloaded" | "cancelled"> {
  const nav = navigator as Navigator & {
    canShare?: (data: { files: File[] }) => boolean;
  };
  if (typeof nav.share === "function" && nav.canShare?.({ files: [file] })) {
    try {
      await nav.share({ files: [file], title: file.name });
      return "shared";
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return "cancelled";
      }
      // Anything else (a browser that advertised files and then refused):
      // fall back to the download below.
    }
  }
  const url = URL.createObjectURL(file);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = file.name;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return "downloaded";
}
